import React, { useMemo, useState } from 'react';
import { Users, Star, DollarSign, ShoppingBag, Repeat, AlertTriangle, MessageSquare, ShieldCheck, Video, Zap, MonitorPlay, Mail, TrendingUp, FileText, CreditCard, UserPlus, Activity, Clock, XCircle, CheckCircle2, BarChart3, RefreshCcw, AlertCircle, Eye, Plus, Calendar, ChevronDown, Search, Filter, Download, ArrowRight, LayoutGrid, List, Wifi, UserCheck, FileCheck, AlertOctagon, Bot } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line } from 'recharts';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface AdminDashboardProps {
  users: any[];
  orders: any[];
  astrologers: any[];
  tickets: any[];
  reviews: any[];
  complaints: any[];
  refunds: any[];
  verificationQueue: any[];
  adRequests: any[];
  userSubscriptions: any[];
  campaigns: any[];
  setActiveTab: (tab: string) => void;
  setCreatePanel: (panel: any) => void;
  selectedPeriod?: 'Сегодня' | 'Вчера' | '7 дней' | '30 дней' | 'Квартал' | 'Год';
}
// Sparkline data
const sparklineUp = [{
  v: 10
}, {
  v: 12
}, {
  v: 11
}, {
  v: 15
}, {
  v: 18
}, {
  v: 22
}, {
  v: 25
}];
const sparklineDown = [{
  v: 25
}, {
  v: 22
}, {
  v: 24
}, {
  v: 18
}, {
  v: 15
}, {
  v: 12
}, {
  v: 10
}];
const sparklineFlat = [{
  v: 15
}, {
  v: 16
}, {
  v: 15
}, {
  v: 14
}, {
  v: 16
}, {
  v: 15
}, {
  v: 15
}];
type Period = 'Сегодня' | 'Вчера' | '7 дней' | '30 дней' | 'Квартал' | 'Год';
export function AdminDashboard({
  users,
  orders,
  astrologers,
  tickets,
  reviews,
  complaints,
  refunds,
  verificationQueue,
  adRequests,
  userSubscriptions,
  campaigns,
  setActiveTab,
  setCreatePanel,
  selectedPeriod: selectedPeriodProp
}: AdminDashboardProps) {
  const [localPeriod, setLocalPeriod] = useState<Period>('Сегодня');
  const selectedPeriod = selectedPeriodProp || localPeriod;
  const [kpiViewMode, setKpiViewMode] = useState<'cards' | 'table'>('cards');
  // Table states
  const [ordersSearch, setOrdersSearch] = useState('');
  const [ordersStatus, setOrdersStatus] = useState('Все');
  const [usersSearch, setUsersSearch] = useState('');
  const [attentionItems, setAttentionItems] = useState([{
    id: 1,
    type: 'Риск-чат',
    object: 'Чат #4092',
    desc: 'Попытка обмена контактами (увод клиента)',
    time: '10 мин назад',
    status: 'new',
    tab: 'messages'
  }, {
    id: 2,
    type: 'Ошибка AI',
    object: 'Отчет #889',
    desc: 'API Timeout при генерации соляра',
    time: '25 мин назад',
    status: 'new',
    tab: 'ai_reports'
  }, {
    id: 3,
    type: 'Жалоба',
    object: 'Заказ #1234',
    desc: 'Клиент недоволен качеством консультации',
    time: '1 час назад',
    status: 'in_progress',
    tab: 'moderation'
  }, {
    id: 4,
    type: 'Платеж',
    object: 'User #8492',
    desc: '3 неудачные попытки оплаты подряд',
    time: '2 часа назад',
    status: 'new',
    tab: 'finance'
  }]);
  const handleStatusChange = (id: number, newStatus: string) => {
    setAttentionItems(items => items.map(item => item.id === id ? {
      ...item,
      status: newStatus
    } : item));
  };
  // Period Multiplier Logic
  const getMultiplier = (period: Period) => {
    switch (period) {
      case 'Сегодня':
        return 1;
      case 'Вчера':
        return 1;
      case '7 дней':
        return 7;
      case '30 дней':
        return 30;
      case 'Квартал':
        return 90;
      case 'Год':
        return 365;
      default:
        return 1;
    }
  };
  // Dynamic KPI Values based on period
  const kpi = useMemo(() => {
    const m = getMultiplier(selectedPeriod);
    return {
      revenue: (142500 * m).toLocaleString('ru-RU') + ' ₽',
      revenueWeek: (1200000 * (m / 7)).toLocaleString('ru-RU', {
        maximumFractionDigits: 0
      }) + ' ₽',
      avgCheck: '4 250 ₽',
      newOrders: Math.floor(342 * m),
      conversion: '4.2%',
      newUsers: Math.floor(124 * m),
      activeSubs: (1240 + Math.floor(m * 2.5)).toLocaleString('ru-RU'),
      newAstrologers: Math.max(1, Math.floor(8 * (m / 7))),
      activeAstrologers: 45 + Math.floor(m / 30),
      onlineAstrologers: Math.max(3, Math.floor(12 + Math.random() * 5)),
      paidOrders: Math.floor(289 * m),
      inProgress: Math.floor(42 * (m > 1 ? 1.5 : 1)),
      cancelled: Math.floor(11 * m),
      refunds: Math.floor(4 * m),
      revenueMonth: (4800000 * (m / 30)).toLocaleString('ru-RU', {
        maximumFractionDigits: 0
      }) + ' ₽',
      complaints: Math.floor((complaints?.length || 6) * (m / 7)),
      reviews: Math.floor((reviews?.filter(r => r.status === 'pending').length || 12) * (m / 7)),
      verification: Math.floor((verificationQueue?.length || 4) * (m / 7)),
      consultations: Math.floor(45 * m),
      aiErrors: Math.floor(3 * m),
      ads: 12 + Math.floor(m / 30),
      campaigns: Math.floor(2 * (m / 7)),
      riskChats: Math.floor(5 * m),
      aiGens: (1450 * m).toLocaleString('ru-RU'),
      problemPayments: Math.floor((refunds?.filter(r => r.status === 'pending').length || 4) * m)
    };
  }, [selectedPeriod, complaints, reviews, verificationQueue, refunds]);
  // Dynamic Chart Data based on period
  const chartData = useMemo(() => {
    const points = selectedPeriod === 'Сегодня' || selectedPeriod === 'Вчера' ? 24 : selectedPeriod === '7 дней' ? 7 : selectedPeriod === '30 дней' ? 30 : selectedPeriod === 'Квартал' ? 12 : 12;
    const labels = selectedPeriod === 'Сегодня' || selectedPeriod === 'Вчера' ? Array.from({
      length: 24
    }).map((_, i) => `${i}:00`) : selectedPeriod === '7 дней' ? ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] : selectedPeriod === '30 дней' ? Array.from({
      length: 30
    }).map((_, i) => `${i + 1}`) : ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
    const generateTrendData = (base: number, variance: number) => Array.from({
      length: points
    }).map((_, i) => ({
      name: labels[i % labels.length],
      value: Math.floor(base + Math.random() * variance)
    }));
    return {
      sales: generateTrendData(120000, 80000),
      users: generateTrendData(100, 50),
      consultations: generateTrendData(30, 20),
      aiReports: generateTrendData(500, 300),
      orders: generateTrendData(200, 100),
      subs: generateTrendData(50, 20)
    };
  }, [selectedPeriod]);
  // Filtered Orders
  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      const matchSearch = o.id.toLowerCase().includes(ordersSearch.toLowerCase()) || o.client.toLowerCase().includes(ordersSearch.toLowerCase()) || o.service.toLowerCase().includes(ordersSearch.toLowerCase());
      const matchStatus = ordersStatus === 'Все' || ordersStatus === 'В работе' && o.status === 'in_progress' || ordersStatus === 'Завершён' && o.status === 'completed' || ordersStatus === 'Отменён' && o.status === 'cancelled';
      return matchSearch && matchStatus;
    }).slice(0, 10);
  }, [orders, ordersSearch, ordersStatus]);
  // Filtered Users
  const filteredUsers = useMemo(() => {
    return users.filter(u => u.name.toLowerCase().includes(usersSearch.toLowerCase()) || u.email.toLowerCase().includes(usersSearch.toLowerCase())).slice(0, 7);
  }, [users, usersSearch]);
  const getOrderStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="pro" size="sm" data-id="element-2983">
            Завершён
          </Badge>;
      case 'in_progress':
        return <Badge variant="warning" size="sm" data-id="element-2984">
            В работе
          </Badge>;
      case 'cancelled':
        return <Badge variant="error" size="sm" data-id="element-2985">
            Отменён
          </Badge>;
      default:
        return <Badge variant="neutral" size="sm" data-id="element-2986">
            {status}
          </Badge>;
    }
  };
  const getUserStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="pro" size="sm" data-id="element-2987">
            Активен
          </Badge>;
      case 'blocked':
        return <Badge variant="error" size="sm" data-id="element-2988">
            Заблокирован
          </Badge>;
      default:
        return <Badge variant="neutral" size="sm" data-id="element-2989">
            {status}
          </Badge>;
    }
  };
  const renderKpiCard = (id: string, label: string, value: string | number, trend: string, icon: React.ReactNode, bgClass: string, textClass: string, sparklineData: any[] = sparklineUp) => {
    const isPositive = trend.startsWith('+');
    const isNegative = trend.startsWith('-');
    const trendColor = isPositive ? 'text-emerald-600 bg-emerald-50' : isNegative ? 'text-rose-600 bg-rose-50' : 'text-slate-600 bg-slate-50';
    const strokeColor = isPositive ? '#10b981' : isNegative ? '#f43f5e' : '#64748b';
    return <Card key={label} padding="sm" hoverable className="rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-32 cursor-pointer group relative overflow-hidden" onClick={() => setActiveTab(id)} data-id="element-2990">
        <div className="flex justify-between items-start z-10" data-id="element-2991">
          <div className={`w-8 h-8 rounded-lg ${bgClass} flex items-center justify-center group-hover:scale-110 transition-transform`} data-id="element-2992">
            {icon}
          </div>
          <div className="flex flex-col items-end" data-id="element-2993">
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-md ${trendColor}`} data-id="element-2994">
              {trend}
            </span>
          </div>
        </div>
        <div className="z-10 mt-2" data-id="element-2995">
          <div className="text-xl font-bold text-slate-900" data-id="element-2996">{value}</div>
          <div className="text-xs text-slate-500 font-medium" data-id="element-2997">{label}</div>
        </div>

        {/* Hover tooltip with trend detail */}
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-200 flex flex-col items-center justify-center z-20 pointer-events-none rounded-xl" data-id="element-2998">
          <div className="text-xl font-bold text-slate-900 mb-1" data-id="element-2999">{value}</div>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${trendColor} mb-1`} data-id="element-3000">
            {trend}
          </span>
          <span className="text-[10px] text-slate-500" data-id="element-3001">к прошлому периоду</span>
        </div>

        {/* Mini Sparkline Background */}
        <div className="absolute bottom-0 left-0 right-0 h-12 opacity-20 group-hover:opacity-40 pointer-events-none transition-opacity" data-id="element-3002">
          <ResponsiveContainer width="100%" height="100%" data-id="element-3003">
            <LineChart data={sparklineData} data-id="element-3004">
              <Line type="monotone" dataKey="v" stroke={strokeColor} strokeWidth={2} dot={false} isAnimationActive={false} data-id="element-3005" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>;
  };
  const periods: Period[] = ['Сегодня', 'Вчера', '7 дней', '30 дней', 'Квартал', 'Год'];
  return <div className="space-y-8 pb-10" data-id="element-3006">
      {/* Date Period Filter - Mobile only (desktop version is in topbar) */}
      <div className="flex lg:hidden flex-wrap items-center gap-2 bg-white p-2 rounded-xl border border-slate-200 shadow-sm" data-id="element-3007">
        <div className="flex items-center gap-2 px-3 text-slate-500" data-id="element-3008">
          <Calendar className="w-4 h-4" data-id="element-3009" />
          <span className="text-xs font-bold uppercase tracking-wider" data-id="element-3010">
            Период:
          </span>
        </div>
        <div className="flex flex-wrap gap-1" data-id="element-3011">
          {periods.map(period => <button key={period} onClick={() => setLocalPeriod(period)} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${selectedPeriod === period ? 'bg-indigo-600 text-white shadow-sm' : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'}`} data-id="element-3012">
              {period}
            </button>)}
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="flex flex-wrap gap-2 items-center bg-white p-2 rounded-xl border border-slate-200 shadow-sm" data-id="element-3013">
        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider px-3" data-id="element-3014">
          Быстрые действия:
        </div>
        <Button size="sm" variant="outline" className="bg-slate-50 hover:bg-slate-100 border-slate-200" icon={<UserPlus className="w-4 h-4" data-id="element-3016" />} onClick={() => setCreatePanel({
        type: 'astrologer',
        title: 'Добавить астролога'
      })} data-id="element-3015">
          Добавить астролога
        </Button>
        <Button size="sm" variant="outline" className="bg-slate-50 hover:bg-slate-100 border-slate-200" icon={<MonitorPlay className="w-4 h-4" data-id="element-3018" />} onClick={() => setCreatePanel({
        type: 'ad',
        title: 'Создать рекламу'
      })} data-id="element-3017">
          Создать рекламу
        </Button>
        <Button size="sm" variant="outline" className="bg-slate-50 hover:bg-slate-100 border-slate-200" icon={<Mail className="w-4 h-4" data-id="element-3020" />} onClick={() => setCreatePanel({
        type: 'campaign',
        title: 'Создать рассылку'
      })} data-id="element-3019">
          Создать рассылку
        </Button>
        <Button size="sm" variant="outline" className="bg-slate-50 hover:bg-slate-100 border-slate-200" icon={<FileText className="w-4 h-4" data-id="element-3022" />} onClick={() => setCreatePanel({
        type: 'content',
        title: 'Создать контент'
      })} data-id="element-3021">
          Создать контент
        </Button>
        <div className="hidden sm:block w-px h-6 bg-slate-200 mx-1" data-id="element-3023"></div>
        <Button size="sm" variant="primary" className="bg-indigo-600 hover:bg-indigo-700" icon={<ShoppingBag className="w-4 h-4" data-id="element-3025" />} onClick={() => setCreatePanel({
        type: 'order',
        title: 'Создать заказ'
      })} data-id="element-3024">
          Создать заказ
        </Button>
        <Button size="sm" variant="primary" className="bg-purple-600 hover:bg-purple-700" icon={<Zap className="w-4 h-4" data-id="element-3027" />} onClick={() => setCreatePanel({
        type: 'ai_report',
        title: 'Создать AI отчёт'
      })} data-id="element-3026">
          Создать AI отчет
        </Button>
      </div>

      {/* REQUIRES ATTENTION BLOCK (CRITICAL) */}
      <div data-id="element-3028">
        <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-3029">
          <AlertTriangle className="w-5 h-5 text-rose-500" data-id="element-3030" /> Требует внимания
        </h3>
        <Card padding="none" className="rounded-xl shadow-sm border-rose-100 overflow-hidden" data-id="element-3031">
          <div className="overflow-x-auto" data-id="element-3032">
            <table className="w-full text-left min-w-[700px]" data-id="element-3033">
              <thead className="bg-rose-50/50 text-slate-600 text-[11px] uppercase tracking-wider border-b border-rose-100" data-id="element-3034">
                <tr data-id="element-3035">
                  <th className="px-4 py-3 font-semibold" data-id="element-3036">Тип проблемы</th>
                  <th className="px-4 py-3 font-semibold" data-id="element-3037">Объект</th>
                  <th className="px-4 py-3 font-semibold" data-id="element-3038">Описание</th>
                  <th className="px-4 py-3 font-semibold" data-id="element-3039">Время</th>
                  <th className="px-4 py-3 font-semibold" data-id="element-3040">Статус</th>
                  <th className="px-4 py-3 text-right font-semibold" data-id="element-3041">
                    Действие
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-3042">
                {attentionItems.map(item => <tr key={item.id} className="hover:bg-slate-50 transition-colors" data-id="element-3043">
                    <td className="px-4 py-3" data-id="element-3044">
                      <Badge variant={item.type === 'Риск-чат' || item.type === 'Жалоба' ? 'error' : 'warning'} size="sm" className={item.type === 'Риск-чат' || item.type === 'Жалоба' ? 'bg-rose-100 text-rose-700 border-rose-200' : 'bg-amber-100 text-amber-700 border-amber-200'} data-id="element-3045">
                        {item.type}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900" data-id="element-3046">
                      {item.object}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600" data-id="element-3047">
                      {item.desc}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500" data-id="element-3048">
                      {item.time}
                    </td>
                    <td className="px-4 py-3" data-id="element-3049">
                      <div className="relative inline-block w-32" data-id="element-3050">
                        <select className={`w-full appearance-none rounded-lg border px-3 py-1.5 text-xs font-medium outline-none transition-colors cursor-pointer ${item.status === 'resolved' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : item.status === 'in_progress' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`} value={item.status} onChange={e => handleStatusChange(item.id, e.target.value)} data-id="element-3051">
                          <option value="new" data-id="element-3052">Новая</option>
                          <option value="in_progress" data-id="element-3053">В работе</option>
                          <option value="resolved" data-id="element-3054">Решена</option>
                        </select>
                        <ChevronDown className={`absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none ${item.status === 'resolved' ? 'text-emerald-500' : item.status === 'in_progress' ? 'text-blue-500' : 'text-slate-400'}`} data-id="element-3055" />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-3056">
                      <Button size="sm" variant="outline" onClick={() => setActiveTab(item.tab)} data-id="element-3057">
                        Проверить
                      </Button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* KPI Section 1: Growth & Finance */}
      <div data-id="element-3058">
        <div className="flex items-center justify-between mb-3" data-id="element-3059">
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2" data-id="element-3060">
            <TrendingUp className="w-5 h-5 text-indigo-500" data-id="element-3061" /> Рост и Финансы
          </h3>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-id="element-3062">
            <button onClick={() => setKpiViewMode('cards')} className={`p-1.5 rounded-md transition-colors ${kpiViewMode === 'cards' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`} title="Карточки" data-id="element-3063">
              <LayoutGrid className="w-4 h-4" data-id="element-3064" />
            </button>
            <button onClick={() => setKpiViewMode('table')} className={`p-1.5 rounded-md transition-colors ${kpiViewMode === 'table' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`} title="Таблица" data-id="element-3065">
              <List className="w-4 h-4" data-id="element-3066" />
            </button>
          </div>
        </div>

        {kpiViewMode === 'cards' ? <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-id="element-3067">
            {renderKpiCard('finance', `Выручка (${selectedPeriod.toLowerCase()})`, kpi.revenue, '+5%', <DollarSign className="w-4 h-4 text-emerald-600" data-id="element-3068" />, 'bg-emerald-50', 'text-emerald-600', sparklineUp)}
            {renderKpiCard('finance', 'Выручка (неделя)', kpi.revenueWeek, '+15%', <TrendingUp className="w-4 h-4 text-emerald-600" data-id="element-3069" />, 'bg-emerald-50', 'text-emerald-600', sparklineUp)}
            {renderKpiCard('finance', 'Средний чек', kpi.avgCheck, '+2%', <CreditCard className="w-4 h-4 text-indigo-600" data-id="element-3070" />, 'bg-indigo-50', 'text-indigo-600', sparklineFlat)}
            {renderKpiCard('orders', 'Новые заказы', kpi.newOrders, '+18%', <ShoppingBag className="w-4 h-4 text-indigo-600" data-id="element-3071" />, 'bg-indigo-50', 'text-indigo-600', sparklineUp)}
            {renderKpiCard('orders', 'Конверсия', kpi.conversion, '+0.5%', <Activity className="w-4 h-4 text-indigo-600" data-id="element-3072" />, 'bg-indigo-50', 'text-indigo-600', sparklineUp)}
            {renderKpiCard('users', 'Новые юзеры', kpi.newUsers, '+12%', <Users className="w-4 h-4 text-blue-600" data-id="element-3073" />, 'bg-blue-50', 'text-blue-600', sparklineUp)}
            {renderKpiCard('subscriptions', 'Активные подписки', kpi.activeSubs, '+8%', <Repeat className="w-4 h-4 text-blue-600" data-id="element-3074" />, 'bg-blue-50', 'text-blue-600', sparklineUp)}
            {renderKpiCard('astrologers', 'Новые астрологи', kpi.newAstrologers, '+2', <Star className="w-4 h-4 text-amber-600" data-id="element-3075" />, 'bg-amber-50', 'text-amber-600', sparklineUp)}
            {renderKpiCard('astrologers', 'Активные астрологи', kpi.activeAstrologers, '+5', <Video className="w-4 h-4 text-amber-600" data-id="element-3076" />, 'bg-amber-50', 'text-amber-600', sparklineUp)}
            <Card key="online-astrologers" padding="sm" hoverable className="rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-32 cursor-pointer group relative overflow-hidden border-l-2 border-l-emerald-400" onClick={() => setActiveTab('consultations')} data-id="element-3077">
              <div className="flex justify-between items-start z-10" data-id="element-3078">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-3079">
                  <Wifi className="w-4 h-4 text-emerald-600" data-id="element-3080" />
                </div>
                <div className="flex items-center gap-1.5" data-id="element-3081">
                  <span className="relative flex h-2.5 w-2.5" data-id="element-3082">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" data-id="element-3083"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" data-id="element-3084"></span>
                  </span>
                  <span className="text-[10px] font-bold text-emerald-600" data-id="element-3085">
                    LIVE
                  </span>
                </div>
              </div>
              <div className="z-10 mt-2" data-id="element-3086">
                <div className="text-xl font-bold text-slate-900" data-id="element-3087">
                  {kpi.onlineAstrologers}
                </div>
                <div className="text-xs text-slate-500 font-medium" data-id="element-3088">
                  Онлайн астрологи
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-12 opacity-20 pointer-events-none" data-id="element-3089">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3090">
                  <LineChart data={sparklineUp} data-id="element-3091">
                    <Line type="monotone" dataKey="v" stroke="#10b981" strokeWidth={2} dot={false} isAnimationActive={false} data-id="element-3092" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
            {renderKpiCard('orders', 'Оплаченные', kpi.paidOrders, '+14%', <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-3093" />, 'bg-emerald-50', 'text-emerald-600', sparklineUp)}
            {renderKpiCard('orders', 'В работе', kpi.inProgress, '-5', <Clock className="w-4 h-4 text-amber-600" data-id="element-3094" />, 'bg-amber-50', 'text-amber-600', sparklineFlat)}
            {renderKpiCard('orders', 'Отмены', kpi.cancelled, '-2', <XCircle className="w-4 h-4 text-rose-600" data-id="element-3095" />, 'bg-rose-50', 'text-rose-600', sparklineDown)}
            {renderKpiCard('finance', 'Возвраты', kpi.refunds, '0', <RefreshCcw className="w-4 h-4 text-rose-600" data-id="element-3096" />, 'bg-rose-50', 'text-rose-600', sparklineFlat)}
            {renderKpiCard('finance', 'Выручка (месяц)', kpi.revenueMonth, '+22%', <DollarSign className="w-4 h-4 text-emerald-600" data-id="element-3097" />, 'bg-emerald-50', 'text-emerald-600', sparklineUp)}
          </div> : <Card padding="none" className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-id="element-3098">
            <div className="overflow-x-auto" data-id="element-3099">
              <table className="w-full text-left min-w-[600px]" data-id="element-3100">
                <thead className="bg-slate-50/50 text-slate-500 text-[11px] uppercase tracking-wider border-b border-slate-100" data-id="element-3101">
                  <tr data-id="element-3102">
                    <th className="px-4 py-3 font-semibold" data-id="element-3103">Показатель</th>
                    <th className="px-4 py-3 font-semibold text-right" data-id="element-3104">
                      Значение
                    </th>
                    <th className="px-4 py-3 font-semibold text-right" data-id="element-3105">
                      Тренд
                    </th>
                    <th className="px-4 py-3 font-semibold" data-id="element-3106">Раздел</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50" data-id="element-3107">
                  {[{
                label: `Выручка (${selectedPeriod.toLowerCase()})`,
                value: kpi.revenue,
                trend: '+5%',
                tab: 'finance'
              }, {
                label: 'Выручка (неделя)',
                value: kpi.revenueWeek,
                trend: '+15%',
                tab: 'finance'
              }, {
                label: 'Средний чек',
                value: kpi.avgCheck,
                trend: '+2%',
                tab: 'finance'
              }, {
                label: 'Новые заказы',
                value: kpi.newOrders,
                trend: '+18%',
                tab: 'orders'
              }, {
                label: 'Конверсия',
                value: kpi.conversion,
                trend: '+0.5%',
                tab: 'orders'
              }, {
                label: 'Новые юзеры',
                value: kpi.newUsers,
                trend: '+12%',
                tab: 'users'
              }, {
                label: 'Активные подписки',
                value: kpi.activeSubs,
                trend: '+8%',
                tab: 'subscriptions'
              }, {
                label: 'Новые астрологи',
                value: kpi.newAstrologers,
                trend: '+2',
                tab: 'astrologers'
              }, {
                label: 'Активные астрологи',
                value: kpi.activeAstrologers,
                trend: '+5',
                tab: 'astrologers'
              }, {
                label: 'Оплаченные',
                value: kpi.paidOrders,
                trend: '+14%',
                tab: 'orders'
              }, {
                label: 'В работе',
                value: kpi.inProgress,
                trend: '-5',
                tab: 'orders'
              }, {
                label: 'Отмены',
                value: kpi.cancelled,
                trend: '-2',
                tab: 'orders'
              }, {
                label: 'Возвраты',
                value: kpi.refunds,
                trend: '0',
                tab: 'finance'
              }, {
                label: 'Выручка (месяц)',
                value: kpi.revenueMonth,
                trend: '+22%',
                tab: 'finance'
              }].map((row, i) => {
                const isPositive = row.trend.startsWith('+');
                const isNegative = row.trend.startsWith('-');
                return <tr key={i} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setActiveTab(row.tab)} data-id="element-3108">
                        <td className="px-4 py-2.5 text-sm font-medium text-slate-900" data-id="element-3109">
                          {row.label}
                        </td>
                        <td className="px-4 py-2.5 text-sm font-bold text-slate-900 text-right" data-id="element-3110">
                          {row.value}
                        </td>
                        <td className="px-4 py-2.5 text-right" data-id="element-3111">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${isPositive ? 'text-emerald-600 bg-emerald-50' : isNegative ? 'text-rose-600 bg-rose-50' : 'text-slate-600 bg-slate-50'}`} data-id="element-3112">
                            {row.trend}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate-500 capitalize" data-id="element-3113">
                          {row.tab}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </Card>}
      </div>

      {/* KPI Section 2: Operations & Moderation */}
      <div data-id="element-3114">
        <div className="flex items-center justify-between mb-3" data-id="element-3115">
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2" data-id="element-3116">
            <Activity className="w-5 h-5 text-amber-500" data-id="element-3117" /> Операции и Модерация
          </h3>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-id="element-3118">
            <button onClick={() => setKpiViewMode('cards')} className={`p-1.5 rounded-md transition-colors ${kpiViewMode === 'cards' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`} title="Карточки" data-id="element-3119">
              <LayoutGrid className="w-4 h-4" data-id="element-3120" />
            </button>
            <button onClick={() => setKpiViewMode('table')} className={`p-1.5 rounded-md transition-colors ${kpiViewMode === 'table' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`} title="Таблица" data-id="element-3121">
              <List className="w-4 h-4" data-id="element-3122" />
            </button>
          </div>
        </div>

        {kpiViewMode === 'cards' ? <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-id="element-3123">
            {renderKpiCard('moderation', 'Новые жалобы', kpi.complaints, '+2', <AlertTriangle className="w-4 h-4 text-rose-600" data-id="element-3124" />, 'bg-rose-50', 'text-rose-600', sparklineUp)}
            {renderKpiCard('moderation', 'Новые отзывы', kpi.reviews, '0', <MessageSquare className="w-4 h-4 text-indigo-600" data-id="element-3125" />, 'bg-indigo-50', 'text-indigo-600', sparklineFlat)}
            {renderKpiCard('verification', 'Ожидают проверку', kpi.verification, '-1', <ShieldCheck className="w-4 h-4 text-amber-600" data-id="element-3126" />, 'bg-amber-50', 'text-amber-600', sparklineDown)}
            {renderKpiCard('consultations', 'Консультации', kpi.consultations, '+12', <Video className="w-4 h-4 text-purple-600" data-id="element-3127" />, 'bg-purple-50', 'text-purple-600', sparklineUp)}
            {renderKpiCard('ai_reports', 'Ошибки AI', kpi.aiErrors, '-2', <Zap className="w-4 h-4 text-rose-600" data-id="element-3128" />, 'bg-rose-50', 'text-rose-600', sparklineDown)}
            {renderKpiCard('advertising', 'Активные размещения', kpi.ads, '+3', <MonitorPlay className="w-4 h-4 text-blue-600" data-id="element-3129" />, 'bg-blue-50', 'text-blue-600', sparklineUp)}
            {renderKpiCard('marketing', 'Email кампании', kpi.campaigns, '0', <Mail className="w-4 h-4 text-slate-600" data-id="element-3130" />, 'bg-slate-50', 'text-slate-600', sparklineFlat)}
            {renderKpiCard('messages', 'Риск-диалоги', kpi.riskChats, '+1', <AlertTriangle className="w-4 h-4 text-rose-600" data-id="element-3131" />, 'bg-rose-50', 'text-rose-600', sparklineUp)}
            {renderKpiCard('ai_reports', 'AI Генерации', kpi.aiGens, '+25%', <Zap className="w-4 h-4 text-amber-600" data-id="element-3132" />, 'bg-amber-50', 'text-amber-600', sparklineUp)}
            {renderKpiCard('finance', 'Проблемные платежи', kpi.problemPayments, '+1', <AlertCircle className="w-4 h-4 text-rose-600" data-id="element-3133" />, 'bg-rose-50', 'text-rose-600', sparklineUp)}
          </div> : <Card padding="none" className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-id="element-3134">
            <div className="overflow-x-auto" data-id="element-3135">
              <table className="w-full text-left min-w-[600px]" data-id="element-3136">
                <thead className="bg-slate-50/50 text-slate-500 text-[11px] uppercase tracking-wider border-b border-slate-100" data-id="element-3137">
                  <tr data-id="element-3138">
                    <th className="px-4 py-3 font-semibold" data-id="element-3139">Показатель</th>
                    <th className="px-4 py-3 font-semibold text-right" data-id="element-3140">
                      Значение
                    </th>
                    <th className="px-4 py-3 font-semibold text-right" data-id="element-3141">
                      Тренд
                    </th>
                    <th className="px-4 py-3 font-semibold" data-id="element-3142">Раздел</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50" data-id="element-3143">
                  {[{
                label: 'Новые жалобы',
                value: kpi.complaints,
                trend: '+2',
                tab: 'moderation'
              }, {
                label: 'Новые отзывы',
                value: kpi.reviews,
                trend: '0',
                tab: 'moderation'
              }, {
                label: 'Ожидают проверку',
                value: kpi.verification,
                trend: '-1',
                tab: 'verification'
              }, {
                label: 'Консультации',
                value: kpi.consultations,
                trend: '+12',
                tab: 'consultations'
              }, {
                label: 'Ошибки AI',
                value: kpi.aiErrors,
                trend: '-2',
                tab: 'ai_reports'
              }, {
                label: 'Активные размещения',
                value: kpi.ads,
                trend: '+3',
                tab: 'advertising'
              }, {
                label: 'Email кампании',
                value: kpi.campaigns,
                trend: '0',
                tab: 'marketing'
              }, {
                label: 'Риск-диалоги',
                value: kpi.riskChats,
                trend: '+1',
                tab: 'messages'
              }, {
                label: 'AI Генерации',
                value: kpi.aiGens,
                trend: '+25%',
                tab: 'ai_reports'
              }, {
                label: 'Проблемные платежи',
                value: kpi.problemPayments,
                trend: '+1',
                tab: 'finance'
              }].map((row, i) => {
                const isPositive = row.trend.startsWith('+');
                const isNegative = row.trend.startsWith('-');
                return <tr key={i} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setActiveTab(row.tab)} data-id="element-3144">
                        <td className="px-4 py-2.5 text-sm font-medium text-slate-900" data-id="element-3145">
                          {row.label}
                        </td>
                        <td className="px-4 py-2.5 text-sm font-bold text-slate-900 text-right" data-id="element-3146">
                          {row.value}
                        </td>
                        <td className="px-4 py-2.5 text-right" data-id="element-3147">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${isPositive ? 'text-emerald-600 bg-emerald-50' : isNegative ? 'text-rose-600 bg-rose-50' : 'text-slate-600 bg-slate-50'}`} data-id="element-3148">
                            {row.trend}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate-500 capitalize" data-id="element-3149">
                          {row.tab}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </Card>}
      </div>

      {/* TOP LISTS SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3150">
        {/* Top Astrologers */}
        <Card padding="none" className="rounded-xl shadow-sm border-slate-200 flex flex-col h-[350px]" data-id="element-3151">
          <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50" data-id="element-3152">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2" data-id="element-3153">
              <Star className="w-4 h-4 text-amber-500" data-id="element-3154" /> ТОП Астрологи (
              {selectedPeriod.toLowerCase()})
            </h3>
            <Button variant="ghost" size="sm" className="text-[10px] h-6 px-2" onClick={() => setActiveTab('astrologers')} data-id="element-3155">
              Все
            </Button>
          </div>
          <div className="overflow-y-auto flex-grow" data-id="element-3156">
            <table className="w-full text-left" data-id="element-3157">
              <thead className="bg-white text-slate-400 text-[10px] uppercase tracking-wider sticky top-0 border-b border-slate-100" data-id="element-3158">
                <tr data-id="element-3159">
                  <th className="px-4 py-2 font-medium" data-id="element-3160">Имя</th>
                  <th className="px-4 py-2 font-medium text-center" data-id="element-3161">Заказы</th>
                  <th className="px-4 py-2 font-medium text-right" data-id="element-3162">Выручка</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50" data-id="element-3163">
                {[{
                name: 'Елена С.',
                orders: 12,
                rev: '45 000 ₽'
              }, {
                name: 'Михаил В.',
                orders: 9,
                rev: '32 500 ₽'
              }, {
                name: 'Анна К.',
                orders: 8,
                rev: '28 000 ₽'
              }, {
                name: 'Дмитрий Р.',
                orders: 6,
                rev: '21 000 ₽'
              }, {
                name: 'Ольга М.',
                orders: 5,
                rev: '18 500 ₽'
              }].map((a, i) => <tr key={i} className="hover:bg-slate-50 cursor-pointer" onClick={() => setActiveTab('astrologers')} data-id="element-3164">
                    <td className="px-4 py-2.5 text-xs font-medium text-slate-900" data-id="element-3165">
                      {a.name}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 text-center" data-id="element-3166">
                      {Math.floor(a.orders * getMultiplier(selectedPeriod))}
                    </td>
                    <td className="px-4 py-2.5 text-xs font-bold text-emerald-600 text-right" data-id="element-3167">
                      {a.rev}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Top Services */}
        <Card padding="none" className="rounded-xl shadow-sm border-slate-200 flex flex-col h-[350px]" data-id="element-3168">
          <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50" data-id="element-3169">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2" data-id="element-3170">
              <ShoppingBag className="w-4 h-4 text-indigo-500" data-id="element-3171" /> ТОП Услуги
            </h3>
            <Button variant="ghost" size="sm" className="text-[10px] h-6 px-2" onClick={() => setActiveTab('services')} data-id="element-3172">
              Все
            </Button>
          </div>
          <div className="overflow-y-auto flex-grow" data-id="element-3173">
            <table className="w-full text-left" data-id="element-3174">
              <thead className="bg-white text-slate-400 text-[10px] uppercase tracking-wider sticky top-0 border-b border-slate-100" data-id="element-3175">
                <tr data-id="element-3176">
                  <th className="px-4 py-2 font-medium" data-id="element-3177">Услуга</th>
                  <th className="px-4 py-2 font-medium text-center" data-id="element-3178">Кол-во</th>
                  <th className="px-4 py-2 font-medium text-right" data-id="element-3179">
                    Конверсия
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50" data-id="element-3180">
                {[{
                name: 'Натальная карта (AI)',
                qty: 145,
                conv: '12.4%'
              }, {
                name: 'Совместимость',
                qty: 89,
                conv: '8.2%'
              }, {
                name: 'Консультация 60 мин',
                qty: 42,
                conv: '4.1%'
              }, {
                name: 'Соляр на год',
                qty: 38,
                conv: '5.5%'
              }, {
                name: 'Детский гороскоп',
                qty: 24,
                conv: '3.8%'
              }].map((s, i) => <tr key={i} className="hover:bg-slate-50 cursor-pointer" onClick={() => setActiveTab('services')} data-id="element-3181">
                    <td className="px-4 py-2.5 text-xs font-medium text-slate-900 truncate max-w-[120px]" data-id="element-3182">
                      {s.name}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 text-center" data-id="element-3183">
                      {Math.floor(s.qty * getMultiplier(selectedPeriod))}
                    </td>
                    <td className="px-4 py-2.5 text-xs font-bold text-indigo-600 text-right" data-id="element-3184">
                      {s.conv}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Complaints Breakdown */}
        <Card padding="none" className="rounded-xl shadow-sm border-slate-200 flex flex-col h-[350px]" data-id="element-3185">
          <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50" data-id="element-3186">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2" data-id="element-3187">
              <AlertTriangle className="w-4 h-4 text-rose-500" data-id="element-3188" /> Статус жалоб
            </h3>
            <Button variant="ghost" size="sm" className="text-[10px] h-6 px-2" onClick={() => setActiveTab('moderation')} data-id="element-3189">
              Все
            </Button>
          </div>
          <div className="p-5 flex-grow flex flex-col justify-center gap-4" data-id="element-3190">
            <div className="flex items-center justify-between p-3 bg-rose-50 rounded-lg border border-rose-100 cursor-pointer hover:bg-rose-100 transition-colors" onClick={() => setActiveTab('moderation')} data-id="element-3191">
              <div className="flex items-center gap-3" data-id="element-3192">
                <div className="w-2 h-2 rounded-full bg-rose-500" data-id="element-3193"></div>
                <span className="text-sm font-medium text-rose-900" data-id="element-3194">
                  Критические
                </span>
              </div>
              <span className="text-lg font-bold text-rose-700" data-id="element-3195">2</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100 cursor-pointer hover:bg-amber-100 transition-colors" onClick={() => setActiveTab('moderation')} data-id="element-3196">
              <div className="flex items-center gap-3" data-id="element-3197">
                <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-3198"></div>
                <span className="text-sm font-medium text-amber-900" data-id="element-3199">
                  Новые (необработанные)
                </span>
              </div>
              <span className="text-lg font-bold text-amber-700" data-id="element-3200">6</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100 cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => setActiveTab('moderation')} data-id="element-3201">
              <div className="flex items-center gap-3" data-id="element-3202">
                <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-3203"></div>
                <span className="text-sm font-medium text-blue-900" data-id="element-3204">
                  В работе
                </span>
              </div>
              <span className="text-lg font-bold text-blue-700" data-id="element-3205">4</span>
            </div>
          </div>
        </Card>
      </div>

      {/* DATA TABLES SECTION */}
      <div className="space-y-6" data-id="element-3206">
        {/* Orders Table */}
        <Card padding="none" className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-id="element-3207">
          <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50/50" data-id="element-3208">
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2" data-id="element-3209">
              <ShoppingBag className="w-5 h-5 text-indigo-500" data-id="element-3210" /> Последние
              заказы
            </h3>
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto" data-id="element-3211">
              <div className="relative w-full sm:w-64" data-id="element-3212">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3213" />
                <input type="text" placeholder="Поиск по ID, клиенту, услуге..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 bg-white" value={ordersSearch} onChange={e => setOrdersSearch(e.target.value)} data-id="element-3214" />
              </div>
              <div className="flex gap-2 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0" data-id="element-3215">
                {['Все', 'В работе', 'Завершён', 'Отменён'].map(status => <button key={status} onClick={() => setOrdersStatus(status)} className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${ordersStatus === status ? 'bg-indigo-100 text-indigo-700' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`} data-id="element-3216">
                    {status}
                  </button>)}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto" data-id="element-3217">
            <table className="w-full text-left min-w-[800px]" data-id="element-3218">
              <thead className="bg-white text-slate-400 text-[11px] uppercase tracking-wider border-b border-slate-100" data-id="element-3219">
                <tr data-id="element-3220">
                  <th className="px-4 py-3 font-medium" data-id="element-3221">ID Заказа</th>
                  <th className="px-4 py-3 font-medium" data-id="element-3222">Клиент</th>
                  <th className="px-4 py-3 font-medium" data-id="element-3223">Услуга</th>
                  <th className="px-4 py-3 font-medium" data-id="element-3224">Сумма</th>
                  <th className="px-4 py-3 font-medium" data-id="element-3225">Статус</th>
                  <th className="px-4 py-3 font-medium" data-id="element-3226">Дата</th>
                  <th className="px-4 py-3 text-right font-medium" data-id="element-3227">Действие</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50" data-id="element-3228">
                {filteredOrders.length > 0 ? filteredOrders.map((order, i) => <tr key={i} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setActiveTab('orders')} data-id="element-3229">
                      <td className="px-4 py-3 text-sm font-medium text-indigo-600" data-id="element-3230">
                        {order.id}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900" data-id="element-3231">
                        {order.client}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600" data-id="element-3232">
                        {order.service}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-slate-900" data-id="element-3233">
                        {order.price}
                      </td>
                      <td className="px-4 py-3" data-id="element-3234">
                        {getOrderStatusBadge(order.status)}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500" data-id="element-3235">
                        {order.date}
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-3236">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={e => {
                    e.stopPropagation();
                    setActiveTab('orders');
                  }} data-id="element-3237">
                          <ArrowRight className="w-4 h-4 text-slate-400" data-id="element-3238" />
                        </Button>
                      </td>
                    </tr>) : <tr data-id="element-3239">
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500" data-id="element-3240">
                      Заказы не найдены
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center text-xs text-slate-500" data-id="element-3241">
            <span data-id="element-3242">Показано {filteredOrders.length} заказов</span>
            <Button variant="outline" size="sm" onClick={() => setActiveTab('orders')} data-id="element-3243">
              Все заказы
            </Button>
          </div>
        </Card>

        {/* Users Table */}
        <Card padding="none" className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-id="element-3244">
          <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50/50" data-id="element-3245">
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2" data-id="element-3246">
              <Users className="w-5 h-5 text-blue-500" data-id="element-3247" /> Новые пользователи
            </h3>
            <div className="relative w-full sm:w-64" data-id="element-3248">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3249" />
              <input type="text" placeholder="Поиск по имени, email..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 bg-white" value={usersSearch} onChange={e => setUsersSearch(e.target.value)} data-id="element-3250" />
            </div>
          </div>
          <div className="overflow-x-auto" data-id="element-3251">
            <table className="w-full text-left min-w-[800px]" data-id="element-3252">
              <thead className="bg-white text-slate-400 text-[11px] uppercase tracking-wider border-b border-slate-100" data-id="element-3253">
                <tr data-id="element-3254">
                  <th className="px-4 py-3 font-medium" data-id="element-3255">Имя</th>
                  <th className="px-4 py-3 font-medium" data-id="element-3256">Email</th>
                  <th className="px-4 py-3 font-medium" data-id="element-3257">Дата регистрации</th>
                  <th className="px-4 py-3 font-medium text-center" data-id="element-3258">Заказы</th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-3259">
                    Потрачено
                  </th>
                  <th className="px-4 py-3 font-medium text-center" data-id="element-3260">Статус</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50" data-id="element-3261">
                {filteredUsers.length > 0 ? filteredUsers.map((user, i) => <tr key={i} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setActiveTab('users')} data-id="element-3262">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900" data-id="element-3263">
                        {user.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500" data-id="element-3264">
                        {user.email}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500" data-id="element-3265">
                        {user.date}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-center" data-id="element-3266">
                        {user.orders}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-emerald-600 text-right" data-id="element-3267">
                        {user.spent}
                      </td>
                      <td className="px-4 py-3 text-center" data-id="element-3268">
                        {getUserStatusBadge(user.status)}
                      </td>
                    </tr>) : <tr data-id="element-3269">
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500" data-id="element-3270">
                      Пользователи не найдены
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center text-xs text-slate-500" data-id="element-3271">
            <span data-id="element-3272">Показано {filteredUsers.length} пользователей</span>
            <Button variant="outline" size="sm" onClick={() => setActiveTab('users')} data-id="element-3273">
              Все пользователи
            </Button>
          </div>
        </Card>
      </div>

      {/* RECENT EVENTS FEED */}
      <Card padding="none" className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-id="element-3274">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50" data-id="element-3275">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2" data-id="element-3276">
            <Activity className="w-4 h-4 text-indigo-500" data-id="element-3277" /> Последние события
          </h3>
          <div className="flex items-center gap-2" data-id="element-3278">
            <span className="relative flex h-2 w-2" data-id="element-3279">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" data-id="element-3280"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" data-id="element-3281"></span>
            </span>
            <span className="text-[10px] font-medium text-slate-500" data-id="element-3282">
              В реальном времени
            </span>
          </div>
        </div>
        <div className="divide-y divide-slate-50" data-id="element-3283">
          {[{
          icon: <UserCheck className="w-3.5 h-3.5 text-blue-500" data-id="element-3284" />,
          bg: 'bg-blue-50',
          type: 'Регистрация',
          desc: 'Новый пользователь Анна К. зарегистрирован',
          time: '2 мин назад',
          tab: 'users'
        }, {
          icon: <ShoppingBag className="w-3.5 h-3.5 text-indigo-500" data-id="element-3285" />,
          bg: 'bg-indigo-50',
          type: 'Заказ',
          desc: 'Заказ #ORD-1247 создан — Натальная карта (₽3,900)',
          time: '5 мин назад',
          tab: 'orders'
        }, {
          icon: <Bot className="w-3.5 h-3.5 text-violet-500" data-id="element-3286" />,
          bg: 'bg-violet-50',
          type: 'AI отчёт',
          desc: 'Отчёт #RPT-892 сгенерирован — Соляр (38 стр.)',
          time: '8 мин назад',
          tab: 'ai_reports'
        }, {
          icon: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" data-id="element-3287" />,
          bg: 'bg-emerald-50',
          type: 'Оплата',
          desc: 'Оплата ₽7,800 получена от Дмитрий П.',
          time: '12 мин назад',
          tab: 'finance'
        }, {
          icon: <AlertOctagon className="w-3.5 h-3.5 text-rose-500" data-id="element-3288" />,
          bg: 'bg-rose-50',
          type: 'Жалоба',
          desc: 'Жалоба #CMP-34 создана — Качество отчёта',
          time: '18 мин назад',
          tab: 'moderation'
        }, {
          icon: <Star className="w-3.5 h-3.5 text-amber-500" data-id="element-3289" />,
          bg: 'bg-amber-50',
          type: 'Отзыв',
          desc: 'Новый отзыв ★★★★★ на Мария Звёздная',
          time: '22 мин назад',
          tab: 'moderation'
        }, {
          icon: <Bot className="w-3.5 h-3.5 text-violet-500" data-id="element-3290" />,
          bg: 'bg-violet-50',
          type: 'AI отчёт',
          desc: 'Отчёт #RPT-891 сгенерирован — Комбо (62 стр.)',
          time: '25 мин назад',
          tab: 'ai_reports'
        }, {
          icon: <ShoppingBag className="w-3.5 h-3.5 text-indigo-500" data-id="element-3291" />,
          bg: 'bg-indigo-50',
          type: 'Заказ',
          desc: 'Заказ #ORD-1246 создан — Прогноз транзитов (₽2,900)',
          time: '31 мин назад',
          tab: 'orders'
        }, {
          icon: <UserCheck className="w-3.5 h-3.5 text-blue-500" data-id="element-3292" />,
          bg: 'bg-blue-50',
          type: 'Регистрация',
          desc: 'Новый пользователь Сергей М. зарегистрирован',
          time: '35 мин назад',
          tab: 'users'
        }, {
          icon: <Wifi className="w-3.5 h-3.5 text-emerald-500" data-id="element-3293" />,
          bg: 'bg-emerald-50',
          type: 'Консультация',
          desc: 'Астролог Алексей Л. начал консультацию с User #4521',
          time: '40 мин назад',
          tab: 'consultations'
        }].map((event, i) => <button key={i} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left" onClick={() => setActiveTab(event.tab)} data-id="element-3294">
              <div className={`w-7 h-7 rounded-lg ${event.bg} flex items-center justify-center flex-shrink-0`} data-id="element-3295">
                {event.icon}
              </div>
              <div className="flex-1 min-w-0" data-id="element-3296">
                <div className="flex items-center gap-2" data-id="element-3297">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-3298">
                    {event.type}
                  </span>
                </div>
                <p className="text-xs text-slate-700 truncate" data-id="element-3299">{event.desc}</p>
              </div>
              <span className="text-[10px] text-slate-400 whitespace-nowrap flex-shrink-0" data-id="element-3300">
                {event.time}
              </span>
              <ArrowRight className="w-3 h-3 text-slate-300 flex-shrink-0" data-id="element-3301" />
            </button>)}
        </div>
      </Card>

      {/* CHARTS GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3302">
        {/* Revenue Chart */}
        <Card padding="md" className="rounded-xl shadow-sm border-slate-200" data-id="element-3303">
          <div className="flex justify-between items-center mb-6" data-id="element-3304">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-3305">
              Выручка ({selectedPeriod.toLowerCase()})
            </h3>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setActiveTab('finance')} data-id="element-3306">
              Подробнее
            </Button>
          </div>
          <div className="h-48" data-id="element-3307">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3308">
              <AreaChart data={chartData.sales} data-id="element-3309">
                <defs data-id="element-3310">
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1" data-id="element-3311">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-3312" />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-3313" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-3314" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#64748b'
              }} data-id="element-3315" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#64748b'
              }} tickFormatter={value => `${value / 1000}k`} width={40} data-id="element-3316" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} formatter={(value: number) => [`${value.toLocaleString('ru-RU')} ₽`, 'Выручка']} data-id="element-3317" />
                <Area type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorSales)" data-id="element-3318" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Registrations Chart */}
        <Card padding="md" className="rounded-xl shadow-sm border-slate-200" data-id="element-3319">
          <div className="flex justify-between items-center mb-6" data-id="element-3320">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-3321">Регистрации</h3>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setActiveTab('users')} data-id="element-3322">
              Подробнее
            </Button>
          </div>
          <div className="h-48" data-id="element-3323">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3324">
              <BarChart data={chartData.users} data-id="element-3325">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-3326" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#64748b'
              }} data-id="element-3327" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#64748b'
              }} width={30} data-id="element-3328" />
                <Tooltip cursor={{
                fill: '#f8fafc'
              }} contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-3329" />
                <Bar dataKey="value" fill="#6366f1" radius={[4, 4, 0, 0]} data-id="element-3330" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Orders Chart */}
        <Card padding="md" className="rounded-xl shadow-sm border-slate-200" data-id="element-3331">
          <div className="flex justify-between items-center mb-6" data-id="element-3332">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-3333">Заказы</h3>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setActiveTab('orders')} data-id="element-3334">
              Подробнее
            </Button>
          </div>
          <div className="h-48" data-id="element-3335">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3336">
              <AreaChart data={chartData.orders} data-id="element-3337">
                <defs data-id="element-3338">
                  <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1" data-id="element-3339">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} data-id="element-3340" />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} data-id="element-3341" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-3342" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#64748b'
              }} data-id="element-3343" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#64748b'
              }} width={30} data-id="element-3344" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-3345" />
                <Area type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={2} fillOpacity={1} fill="url(#colorOrders)" data-id="element-3346" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Consultations Chart */}
        <Card padding="md" className="rounded-xl shadow-sm border-slate-200" data-id="element-3347">
          <div className="flex justify-between items-center mb-6" data-id="element-3348">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-3349">Консультации</h3>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setActiveTab('consultations')} data-id="element-3350">
              Подробнее
            </Button>
          </div>
          <div className="h-48" data-id="element-3351">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3352">
              <BarChart data={chartData.consultations} data-id="element-3353">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-3354" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#64748b'
              }} data-id="element-3355" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#64748b'
              }} width={30} data-id="element-3356" />
                <Tooltip cursor={{
                fill: '#f8fafc'
              }} contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-3357" />
                <Bar dataKey="value" fill="#f59e0b" radius={[4, 4, 0, 0]} data-id="element-3358" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* AI Reports Chart */}
        <Card padding="md" className="rounded-xl shadow-sm border-slate-200" data-id="element-3359">
          <div className="flex justify-between items-center mb-6" data-id="element-3360">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-3361">AI Генерации</h3>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setActiveTab('ai_reports')} data-id="element-3362">
              Подробнее
            </Button>
          </div>
          <div className="h-48" data-id="element-3363">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3364">
              <AreaChart data={chartData.aiReports} data-id="element-3365">
                <defs data-id="element-3366">
                  <linearGradient id="colorAI" x1="0" y1="0" x2="0" y2="1" data-id="element-3367">
                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} data-id="element-3368" />
                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} data-id="element-3369" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-3370" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#64748b'
              }} data-id="element-3371" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#64748b'
              }} width={40} data-id="element-3372" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-3373" />
                <Area type="monotone" dataKey="value" stroke="#0ea5e9" strokeWidth={2} fillOpacity={1} fill="url(#colorAI)" data-id="element-3374" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Subscriptions Chart */}
        <Card padding="md" className="rounded-xl shadow-sm border-slate-200" data-id="element-3375">
          <div className="flex justify-between items-center mb-6" data-id="element-3376">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-3377">Рост подписок</h3>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setActiveTab('subscriptions')} data-id="element-3378">
              Подробнее
            </Button>
          </div>
          <div className="h-48" data-id="element-3379">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3380">
              <LineChart data={chartData.subs} data-id="element-3381">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-3382" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#64748b'
              }} data-id="element-3383" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#64748b'
              }} width={30} data-id="element-3384" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-3385" />
                <Line type="monotone" dataKey="value" stroke="#ec4899" strokeWidth={3} dot={{
                r: 4,
                fill: '#ec4899',
                strokeWidth: 0
              }} activeDot={{
                r: 6
              }} data-id="element-3386" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>;
}